import { MoreHorizontal } from 'lucide-react';
import type { NodePath } from '@/components/tree-dnd/tree-dnd-types';
import { useTreeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import { getSiblingMove } from '@/components/tree-dnd/tree-dnd-utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';

interface TreeDndMoveMenuProps {
  path: NodePath;
  siblingCount: number;
}

/** Move up/down in the same parent: the alternative to dragging on a phone. */
export function TreeDndMoveMenu({ path, siblingCount }: TreeDndMoveMenuProps) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  const moveNode = useTreeDndStore((state) => state.moveNode);

  if (!isMobile) return null;

  const items = [
    { direction: 'up', label: LL.audiences.filters.moveUp() },
    { direction: 'down', label: LL.audiences.filters.moveDown() },
  ] as const;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="min-h-touch min-w-touch shrink-0 text-muted-foreground"
          aria-label={LL.audiences.filters.moreActions()}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* React clicks bubble out of the portal into the condition box, which would open its editor. */}
      <DropdownMenuContent align="end" data-tree-dnd-no-activate>
        {items.map(({ direction, label }) => {
          const move = getSiblingMove(path, siblingCount, direction);
          return (
            <DropdownMenuItem
              key={direction}
              disabled={!move}
              onSelect={() => {
                if (move) {
                  moveNode(
                    move.sourcePath,
                    move.destinationParentPath,
                    move.destinationIndex
                  );
                }
              }}
            >
              {label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
